
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { validateEmailTLD } from "@/utils/emailValidation";
import { AlertTriangle } from "lucide-react";

interface WaitlistDialogProps {
  eventTitle: string;
  children: React.ReactNode;
}

export const WaitlistDialog = ({ eventTitle, children }: WaitlistDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [emailValidation, setEmailValidation] = useState<{
    isValid: boolean;
    warning?: string;
    suggestion?: string;
    type?: string;
  }>({ isValid: true });
  const { toast } = useToast();

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    
    if (value.includes('@')) {
      const validation = validateEmailTLD(value);
      setEmailValidation(validation);
    } else {
      setEmailValidation({ isValid: true });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Email required",
        description: "Please enter your email address to join the waitlist.",
        variant: "destructive",
      });
      return;
    }

    const validation = validateEmailTLD(email);
    if (!validation.isValid) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Here you would typically send the email to your backend
      // For now, we'll just show a success message
      console.log("Adding to waitlist:", { email, eventTitle });
      
      toast({
        title: "You're on the waitlist! 🎉",
        description: "We'll notify you as soon as tickets become available.",
        duration: 3000,
      });
      
      setEmail("");
      setEmailValidation({ isValid: true });
      setIsOpen(false);
    } catch (error) {
      console.error("Waitlist signup error:", error);
      toast({
        title: "Something went wrong",
        description: "Please try again later.",
        variant: "destructive",
      });
    }
  };

  const handleSuggestionClick = () => {
    if (emailValidation.suggestion) {
      setEmail(emailValidation.suggestion);
      setEmailValidation({ isValid: true });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md bg-white text-black">
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-semibold">
            🎟️ Join the Waitlist
          </DialogTitle>
          <p className="text-center text-gray-600 mt-2">
            Get notified when tickets become available for <strong>{eventTitle}</strong>
          </p>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="Enter your email address"
              required
              className={`
                ${emailValidation.warning ? 'border-yellow-500' : ''}
                ${!emailValidation.isValid ? 'border-red-500' : ''}
              `}
            />
            
            {emailValidation.warning && (
              <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="text-yellow-800">{emailValidation.warning}</p>
                  {emailValidation.suggestion && (
                    <button
                      type="button"
                      onClick={handleSuggestionClick}
                      className="text-yellow-700 underline hover:text-yellow-900 mt-1"
                    >
                      Did you mean: {emailValidation.suggestion}?
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <Button 
            type="submit"
            className="w-full bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 hover:shadow-xl hover:scale-[1.02] text-white font-bold"
          >
            Join Waitlist
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
